import React, { useEffect, useRef, useState } from 'react';
import { DarkMode } from '@aparajita/capacitor-dark-mode';
import { Box, Divider, Flex, SimpleGrid, Switch, Text, useColorMode, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { SHDarkModeSwitchIcon, SHLightModeSwitchIcon } from 'clipsal-cortex-icons/src/custom-icons';

import { useReduxDispatch } from '../../../app/store';
import { SEHomeCard } from '../../../common/components/SEHomeCard';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { IS_NATIVE, IS_RUNNING_CYPRESS_TESTS } from '../../../common/constants';
import { resetEvChargerData } from '../../ev-charger/dashboard/evChargerSlice';
import { mtlsHandshakePreflight } from '../../ev-charger/wifi-configuration/wifi-connect-helper';
import { selectUser } from '../../user/userSlice';
import { AccountMenuButton } from '../AccountMenuButton';
import { toggleFeatureFlagsModal } from '../featureFlagSlice';
import { getTemperatureFormat, getTimeFormat, TemperatureFormat } from './settings-helpers';
import { SettingSwitch } from './SettingSwitch';

export function SettingsHome() {
  const { colorMode, setColorMode } = useColorMode();
  const navigate = useNavigate();
  const dispatch = useReduxDispatch();
  const user = useSelector(selectUser);
  const { t } = useTranslation();
  const isMouseDown = useRef(false);
  const [isDeveloperToolsSwitchVisible, setDeveloperToolsSwitchVisible] = useState(false);
  const [temperatureFormat, setTemperatureFormat] = useState<TemperatureFormat>(getTemperatureFormat());
  const [shiftColorModeAutomatically, setShiftColorModeAutomatically] = useState(
    localStorage.getItem('shiftColorModeAutomatically') === 'true'
  );
  const [shouldEnableDeveloperTools, setDeveloperToolsEnabled] = useState(
    localStorage.getItem('developerToolsEnabled') === 'true'
  );
  const [timeFormat, setTimeFormat] = useState<'24HR' | 'AM/PM'>(getTimeFormat());
  const toast = useToast();

  useEffect(() => {
    localStorage.setItem('temperatureFormat', temperatureFormat);
  }, [temperatureFormat]);

  /* istanbul ignore next -- @preserve */
  async function handleToggleSystemColorMode(event: React.ChangeEvent<HTMLInputElement>) {
    const shiftAutomatically = event.target.checked;
    setShiftColorModeAutomatically(shiftAutomatically);
    localStorage.setItem('shiftColorModeAutomatically', shiftAutomatically.toString());

    if (shiftAutomatically) {
      const isDarkMode = await DarkMode.isDarkMode();
      if (isDarkMode.dark) {
        setColorMode('dark');
      } else {
        setColorMode('light');
      }
    }

    DarkMode.update();
  }

  function handleSetDeveloperToolsEnabled(isEnabled: boolean) {
    setDeveloperToolsEnabled(isEnabled);
    // Set in local storage
    localStorage.setItem('developerToolsEnabled', isEnabled.toString());

    if (!isEnabled) {
      // Reset any simulation state when disabling developer tools.
      dispatch(resetEvChargerData());
    }
  }

  function handleMouseDown() {
    isMouseDown.current = true;

    setTimeout(() => {
      if (isMouseDown.current) {
        setDeveloperToolsSwitchVisible(true);
      }
    }, 3000);
  }

  function handleMouseUp() {
    isMouseDown.current = false;
  }

  useEffect(() => {
    localStorage.setItem('timeFormat', timeFormat);
  }, [timeFormat]);

  return (
    <SlidingAnimationPageBase title={t('Account.settings')} backURL={`../../dashboard?direction=back`}>
      <Box px={5}>
        <Text>{t('Account.home configuration').toUpperCase()}</Text>
        <SEHomeCard pr={0} pl={2} py={0} borderTopLeftRadius="5px">
          <AccountMenuButton
            data-testid="family-members-button"
            onClick={() => {
              navigate('../family-members/view?direction=forward');
            }}
          >
            <Text fontSize={16} fontWeight="normal" overflow="auto">
              {t('Account.family members')}
            </Text>
          </AccountMenuButton>
          {/* Add notification settings back post-MVP */}
          {/* <AccountMenuButton
            data-testid="notification-settings"
            onClick={() => {
              navigate('../notifications?direction=forward');
            }}
          >
            <Text fontSize={16} fontWeight="normal">
              Notifications
            </Text>
          </AccountMenuButton> */}
          <AccountMenuButton
            data-testid="widget-settings"
            onClick={() => {
              navigate('../widgets?direction=forward');
            }}
            borderBottomRightRadius={'25px'}
          >
            <Text fontSize={16} fontWeight="normal" overflow="auto">
              {t('Account.homepage widgets')}
            </Text>
          </AccountMenuButton>

          {(IS_NATIVE || IS_RUNNING_CYPRESS_TESTS) && (
            <AccountMenuButton
              data-testid="wifi-settings-button"
              onClick={() => {
                navigate('../wifi?direction=forward');
              }}
              borderBottomRightRadius={'25px'}
            >
              <Text fontSize={16} fontWeight="normal" overflow="auto">
                {t('Account.wifi settings')}
              </Text>
            </AccountMenuButton>
          )}

          <AccountMenuButton
            data-testid="licenses-button"
            onClick={() => {
              navigate('../licenses');
            }}
          >
            <Text fontSize={16} fontWeight="normal" overflow="auto">
              {t('Account.licenses')}
            </Text>
          </AccountMenuButton>

          <AccountMenuButton
            data-testid="language-button"
            onClick={() => {
              navigate('../language?direction=forward');
            }}
            borderBottomRightRadius={'25px'}
          >
            <Text fontSize={16} fontWeight="normal">
              {t('Common.language')}
            </Text>
          </AccountMenuButton>

          <Box pb={1} pl={3}>
            <SettingSwitch
              my={5}
              onChange={(isChecked) => {
                setTimeFormat(isChecked ? '24HR' : 'AM/PM');
              }}
              isChecked={timeFormat == '24HR'}
              data-testid={'time-format-switch'}
              text={t('Account.show time 24hr')}
            />
            <SettingSwitch
              my={5}
              onChange={(isChecked) => {
                setTemperatureFormat(isChecked ? 'CELSIUS' : 'FAHRENHEIT');
              }}
              isChecked={temperatureFormat === 'CELSIUS'}
              data-testid={'temperature-format-switch'}
              text={t('Account.show temp c')}
            />
          </Box>
        </SEHomeCard>

        <Text
          pt={5}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleMouseUp}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {t('Account.appearance').toUpperCase()}
        </Text>
        <SEHomeCard borderTopLeftRadius={'5px'} mb={5}>
          <SimpleGrid opacity={shiftColorModeAutomatically ? 0.3 : 1} px={2} pt={8} columns={2}>
            <Flex
              data-testid="dark-mode-option"
              data-is-enabled={colorMode === 'dark'}
              onClick={() => {
                if (!shiftColorModeAutomatically) setColorMode('dark');
              }}
              direction="column"
              alignItems="center"
            >
              <SHDarkModeSwitchIcon w="100%" h="170px" isEnabled={colorMode === 'dark'} />
              <Text fontWeight={500} mt={1}>
                {t('Account.dark mode')}
              </Text>
            </Flex>
            <Flex
              data-testid="light-mode-option"
              data-is-enabled={colorMode === 'light'}
              onClick={() => {
                if (!shiftColorModeAutomatically) setColorMode('light');
              }}
              direction="column"
              alignItems="center"
            >
              <SHLightModeSwitchIcon w="100%" h="170px" isEnabled={colorMode === 'light'} />
              <Text fontWeight={500} mt={1}>
                {t('Account.light mode')}
              </Text>
            </Flex>
          </SimpleGrid>

          <Divider borderColor={'rgba(151, 151, 151, 0.3)'} my={4} />

          <Flex align={'center'} justify="space-between" px={6} pb={5}>
            <Text fontSize={18} fontWeight={500} overflow="auto">
              {t('Account.shift auto')}
            </Text>
            <Switch
              isChecked={shiftColorModeAutomatically}
              data-testid="system-color-mode-enabled"
              onChange={handleToggleSystemColorMode}
              size="lg"
              variant="solarGreen"
            />
          </Flex>
        </SEHomeCard>

        {user.role === 'SUPER_ADMIN' && isDeveloperToolsSwitchVisible && (
          <Flex mt={5} align={'center'} justify="space-between" px={8} pb={5}>
            <Text fontSize={18} fontWeight={550}>
              Enable Developer Tools
            </Text>
            <Switch
              isChecked={shouldEnableDeveloperTools}
              onChange={(event) => handleSetDeveloperToolsEnabled(event.target.checked)}
              size="lg"
              variant="solarGreen"
            />
          </Flex>
        )}
        {(user.role === 'SUPER_ADMIN' || user.role === 'STAFF' || IS_RUNNING_CYPRESS_TESTS) && (
          <SEHomeCard pr={0} pl={2} py={0} mb={10} borderTopLeftRadius="5px">
            <AccountMenuButton
              data-testid="experimental-features"
              cursor="pointer"
              onClick={() => {
                dispatch(toggleFeatureFlagsModal(true));
              }}
              includeBottomDivider={false}
            >
              <Text fontSize={16} fontWeight="normal" overflow="auto">
                {t('Feature Flag.title')}
              </Text>
            </AccountMenuButton>
            {/* Used only for testing. // TODO: Remove after the mTLS is fully tested with EVSE */}
            {user.role === 'SUPER_ADMIN' && (
              <>
                <AccountMenuButton
                  data-testid="mtls-hadshake-button"
                  cursor="pointer"
                  onClick={async () => {
                    try {
                      const result = await mtlsHandshakePreflight();
                      toast({
                        title: 'mTLS Handshake Successful',
                        description: `Cert: ${result.clientP12Base64 ? 'Received' : 'Missing'}`,
                        status: 'success',
                        duration: 5000,
                        isClosable: true,
                      });
                    } catch (error) {
                      toast({
                        title: 'mTLS Handshake Failed',
                        description: error instanceof Error ? error.message : 'Unknown error',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                      });
                    }
                  }}
                  includeBottomDivider={false}
                >
                  <Text fontSize={16} fontWeight="normal" overflow="auto">
                    Test mTLS Handshake
                  </Text>
                </AccountMenuButton>
              </>
            )}
          </SEHomeCard>
        )}
      </Box>
    </SlidingAnimationPageBase>
  );
}
